import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { ReactComponent as SearchIcon } from "svg/search-icon-2.svg";
import { ReactComponent as X } from "svg/x.svg";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/client";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";

const FETCH_PLAYED_WITH = gql`
  query FetchPlayedWith($regionId: String!, $summonerName: String!) {
    fetchPlayedWith(regionId: $regionId, summonerName: $summonerName) {
      summonerNames
    }
  }
`;

const MultiFilter = ({ filters, onFilterChange }) => {
  const { summonerName, region } = getSummonerProfileContext();
  const [getSummoners, { loading, data }] = useLazyQuery(FETCH_PLAYED_WITH, {
    ssr: false,
    variables: {
      regionId: region,
      summonerName,
    },
  });

  const { fetchPlayedWith } = data || {};
  const { summonerNames } = fetchPlayedWith || {};

  const { getChampionImg, getChampionName, useChampionMini } = getRiotAssetsContext();
  const { data: championMini } = useChampionMini({ ssr: true });

  const [searchValue, setSearchValue] = useState("");

  const [currentSearchOption, setCurrentSearchOption] = useState();

  const [inputActive, setInputActive] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState(0);

  const [savedWidth, setSavedWidth] = useState();

  const multiFilterRef = useRef();
  const multiFilterLabelRef = useRef();
  const inputRef = useRef();

  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== null);

  useEffect(() => {
    if (inputActive) {
      getSummoners();
      inputRef.current.focus();
    } else {
      setSearchValue("");
      setCurrentSearchOption(undefined);
    }
  }, [inputActive]);

  useEffect(() => {
    const rect = multiFilterLabelRef.current.getBoundingClientRect();

    setSavedWidth(rect.right - rect.left);
  }, [activeFilters.length]);

  const filterOptions = (options) =>
    options
      .filter(({ name }) => {
        const normalizedChampionName = name.toLowerCase().replace(/[^A-Za-z0-9]/g, "");
        const normalizedInput = searchValue.toLowerCase().replace(/[^A-Za-z0-9]/g, "");
        return normalizedChampionName.startsWith(normalizedInput);
      })
      .slice(...(currentSearchOption === undefined ? [0, 4] : [0]));

  const searchOptions = filterOptions([
    {
      value: "championId",
      name: "Champion",
      onClick: (option) => {
        setCurrentSearchOption(option);
        setSearchValue("");
        inputRef.current.focus();
      },
      render: (
        <>
          <div>
            Champion: <span>Ezreal</span>
          </div>
        </>
      ),
    },
    {
      value: "playedWith",
      name: "Played With",
      onClick: (option) => {
        setCurrentSearchOption(option);
        setSearchValue("");
        inputRef.current.focus();
      },
      render: (
        <>
          <div>
            Played With: <span>Doublelift</span>
          </div>
        </>
      ),
    },
  ]);

  const championOptions = filterOptions(
    ((championMini && Object.values(championMini)) || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ key, name }) => ({
        value: key,
        name,
        onClick: (option) => onFilterChange("championId", { value: option.value }),
        render: (
          <>
            <img src={getChampionImg(key)} />
            <div>{getChampionName(key)}</div>
          </>
        ),
      })),
  );

  const summonerOptions = filterOptions(
    summonerNames
      ? [...summonerNames].sort().map((playedWith) => ({
          value: playedWith,
          name: playedWith,
          onClick: (option) => onFilterChange("duoName", { value: option.value }),
          render: (
            <>
              <div>{playedWith}</div>
            </>
          ),
        }))
      : [],
  );

  const allOptions =
    currentSearchOption === undefined
      ? [...searchOptions, ...championOptions, ...summonerOptions]
      : currentSearchOption.value === "championId"
      ? championOptions
      : summonerOptions;

  const onKeyDown = (e) => {
    if (e.key === "Backspace" && searchValue.length === 0) {
      setCurrentSearchOption(undefined);
    }

    if (e.key === "Enter") {
      allOptions[highlightedItem].onClick(allOptions[highlightedItem]);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedItem(Math.abs(allOptions.length + highlightedItem - 1) % allOptions.length);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedItem(Math.abs(allOptions.length + highlightedItem + 1) % allOptions.length);
    }
  };

  useEffect(() => {
    if (window && inputRef.current && inputActive) {
      const outsideClick = (e) => {
        if (
          multiFilterRef.current &&
          e.target !== null &&
          !multiFilterRef.current.contains(e.target) &&
          document.contains(e.target)
        ) {
          setInputActive(false);
        }
      };
      window.addEventListener("click", outsideClick);

      return () => window.removeEventListener("click", outsideClick);
    }
  }, [inputActive]);

  const renderOptions = (options, highlightOffset) =>
    options.map((option, index) => (
      <div
        key={option.value}
        className={classnames("multi-filter-menu-item", highlightOffset + index === highlightedItem && "highlighted")}
        onMouseEnter={() => setHighlightedItem(highlightOffset + index)}
        onClick={() => option.onClick(option)}
      >
        {option.render}
      </div>
    ));

  const renderTags = () =>
    activeFilters.map(([type, value]) => (
      <div className="multi-filter-tag">
        {`${type === "championId" ? "Champion:" : "Played With:"} ${type === "championId" ? getChampionName(value) : value}`}
        <X className="x-icon" onClick={() => onFilterChange(type, { value: null })} />
      </div>
    ));

  return (
    <div className="multi-filter" ref={multiFilterRef}>
      <div
        className="multi-filter-label"
        onClick={() => setInputActive(true)}
        ref={multiFilterLabelRef}
        style={{
          width: inputActive ? `${savedWidth}px` : "auto",
        }}
      >
        {inputActive && currentSearchOption && (
          <div className="multi-filter-tag">
            {currentSearchOption.name}
            <X
              className="x-icon"
              onClick={() => {
                setCurrentSearchOption(undefined);
                inputRef.current.focus();
              }}
            />
          </div>
        )}
        {activeFilters.length === 0 || inputActive ? (
          <input
            placeholder={
              currentSearchOption === undefined
                ? "Search Champion or Played With..."
                : currentSearchOption.value === "championId"
                ? "Search Champion..."
                : "Search Played With..."
            }
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setHighlightedItem(0);
            }}
            onKeyDown={onKeyDown}
            ref={inputRef}
          />
        ) : (
          <div className="multi-filter-label-tags">{renderTags()}</div>
        )}
        <SearchIcon className="search-icon" />
      </div>
      {inputActive && (
        <div className="multi-filter-menu">
          {activeFilters.length > 0 && <div className="multi-filter-menu-tags">{renderTags()}</div>}

          {currentSearchOption === undefined && (
            <>
              {searchOptions.length > 0 && (
                <div className={classnames("multi-filter-menu-title", activeFilters.length > 0 ? "divider" : "")}>
                  SEARCH OPTIONS
                </div>
              )}
              {renderOptions(searchOptions, 0)}
            </>
          )}

          {(currentSearchOption === undefined || currentSearchOption.value === "championId") && (
            <>
              {championOptions.length > 0 && (
                <div
                  className={classnames(
                    "multi-filter-menu-title",
                    (currentSearchOption !== undefined && activeFilters.length === 0) ||
                      activeFilters.length + searchOptions.length === 0
                      ? ""
                      : "divider",
                  )}
                >
                  CHAMPIONS
                </div>
              )}
              {renderOptions(
                championOptions,
                currentSearchOption && currentSearchOption.value === "championId" ? 0 : searchOptions.length,
              )}
            </>
          )}

          {(currentSearchOption === undefined || currentSearchOption.value === "playedWith") && (
            <>
              {summonerOptions.length > 0 && (
                <div
                  className={classnames(
                    "multi-filter-menu-title",
                    (currentSearchOption !== undefined && activeFilters.length === 0) ||
                      activeFilters.length + searchOptions.length + championOptions.length === 0
                      ? ""
                      : "divider",
                  )}
                >
                  PLAYED WITH
                </div>
              )}
              {loading ? (
                <div style={{ padding: "2rem" }}>
                  <div className="spinthatshit-loader">
                    <div className="spinner"></div>
                  </div>
                </div>
              ) : (
                renderOptions(
                  summonerOptions,
                  currentSearchOption && currentSearchOption.value === "playedWith"
                    ? 0
                    : searchOptions.length + championOptions.length,
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiFilter;
